import { z } from 'zod';
import { PhotoModerationStatus } from '../enums.js';
import { IsoDateTimeSchema, S3KeySchema, UuidSchema } from '../primitives.js';

/**
 * Profile photo metadata. Max 3 per account — Gold's "expanded gallery" perk
 * was retired (not a meaningful upgrade incentive). Position 1 is the primary
 * photo — must contain exactly one face and pass Liveness CompareFaces.
 */
export const ProfilePhotoSchema = z.object({
  id: UuidSchema,
  userId: UuidSchema,
  position: z.number().int().min(1).max(3),
  s3KeyOriginal: S3KeySchema,
  s3KeyBlurred: S3KeySchema.nullable(),
  s3KeyThumbClear: S3KeySchema.nullable(),
  s3KeyThumbBlurred: S3KeySchema.nullable(),
  widthPx: z.number().int().positive(),
  heightPx: z.number().int().positive(),
  moderationStatus: PhotoModerationStatus,
  moderationReason: z.string().nullable(),
  /** Perceptual hash for catfish / re-upload detection. */
  phash: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
});
export type ProfilePhoto = z.infer<typeof ProfilePhotoSchema>;

/**
 * Public projection — what gets sent to clients viewing a profile.
 * S3 keys are never sent directly; clients receive signed CloudFront URLs
 * scoped to the viewer-vs-owner state.
 */
export const PublicPhotoSchema = z.object({
  id: UuidSchema,
  position: z.number().int().min(1).max(3),
  /** Signed URL for the variant the viewer is allowed to see (clear or blurred). */
  url: z.string().url(),
  /** Signed URL for the thumbnail variant. */
  thumbnailUrl: z.string().url(),
  /** Whether the served variant is blurred. Lets the client render appropriate UI. */
  isBlurred: z.boolean(),
});
export type PublicPhoto = z.infer<typeof PublicPhotoSchema>;
